import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import './WettkampftagChecklist.css';

// Minimaler Print-Helper (analog DruckDashboard)
const printCSS = `
  body { font-family: Arial, sans-serif; font-size: 9pt; color: #111; margin: 0; padding: 0; }
  h2 { font-size: 12pt; margin: 0 0 4mm; }
  .division { margin-bottom: 6mm; page-break-inside: avoid; }
  .division-title { font-size: 10pt; font-weight: 700; border-bottom: 0.5pt solid #aaa; padding-bottom: 1mm; margin-bottom: 2mm; }
  table { width: 100%; border-collapse: collapse; font-size: 8pt; }
  th { background: #eee; text-align: left; padding: 1mm 2mm; border: 0.3pt solid #ccc; }
  td { padding: 1mm 2mm; border: 0.3pt solid #ccc; }
  tr:nth-child(even) td { background: #f9f9f9; }
  .header { display: flex; justify-content: space-between; margin-bottom: 5mm; border-bottom: 1pt solid #333; padding-bottom: 2mm; }
  @media print { body { margin: 10mm; } }
`;

function printWindow(html) {
  const win = window.open('', '_blank', 'width=900,height=700');
  if (!win) return;
  win.document.write(html);
  win.document.close();
}

function buildStartlistenHTML(data) {
  const { turnier, divisionen } = data;
  const divHTML = divisionen.map(div => {
    const rows = (div.wettkaempfer || []).map((w, i) => `
      <tr>
        <td style="width:8mm;text-align:center">${i + 1}</td>
        <td><strong>${w.nachname}</strong>, ${w.vorname}</td>
        <td>${w.verein_name || '—'}</td>
        <td style="width:20mm">${w.jahrgang || '—'}</td>
        ${div.typ === 'kumite' ? `<td style="width:18mm">${w.gewicht ? w.gewicht + ' kg' : '—'}</td>` : ''}
      </tr>
    `).join('');
    return `
      <div class="division">
        <div class="division-title">${div.name}${div.division_code ? ' · ' + div.division_code : ''} (${(div.wettkaempfer || []).length} Starter)</div>
        <table>
          <thead><tr>
            <th>#</th><th>Athlet</th><th>Verein</th><th>Jg.</th>
            ${div.typ === 'kumite' ? '<th>Gewicht</th>' : ''}
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    `;
  }).join('');

  return `<!DOCTYPE html><html lang="de"><head><meta charset="UTF-8">
    <title>Starterlisten — ${turnier?.name || ''}</title>
    <style>${printCSS}</style></head>
  <body>
    <div class="header">
      <div>
        <strong>${turnier?.name || 'Turnier'}</strong><br>
        <span style="font-size:8pt;color:#555">${turnier?.ort || ''}</span>
      </div>
      <div style="text-align:right;font-size:8pt;color:#555">
        STARTERLISTEN<br>Stand: ${new Date().toLocaleString('de-DE')}
      </div>
    </div>
    ${divHTML}
    <script>window.onload = function() { window.focus(); window.print(); };</script>
  </body></html>`;
}

const CHECKLIST_ITEMS = [
  {
    id: 'anmeldungen',
    icon: '📋',
    title: 'Anmeldungen prüfen & freigeben',
    description: 'Alle eingegangenen Anmeldungen prüfen, bestätigen oder ablehnen.',
    view: 'anmeldungen'
  },
  {
    id: 'einwaage',
    icon: '⚖️',
    title: 'Einwaage durchführen',
    description: 'Gewichtskontrolle für alle Kämpfer durchführen.',
    view: 'einwaage'
  },
  {
    id: 'starterlisten',
    icon: '🖨️',
    title: 'Starterlisten drucken',
    description: 'Starterlisten für alle Divisionen ausdrucken und verteilen.',
    view: 'druck',
    directAction: { label: 'Alle drucken', icon: '🖨️', type: 'print_starterlisten' }
  },
  {
    id: 'brackets',
    icon: '🥊',
    title: 'Brackets erstellen',
    description: 'Kampfaufteilung und KO-Brackets für alle Divisionen generieren.',
    view: 'brackets',
    directAction: { label: 'Auto-generieren', icon: '⚡', type: 'auto_brackets' }
  },
  {
    id: 'formen',
    icon: '🎋',
    title: 'Formen-Starts erstellen',
    description: 'Formen-Kategorien anlegen und Teilnehmer den Starts zuweisen.',
    view: 'formen',
    directAction: { label: 'Auto-generieren', icon: '⚡', type: 'auto_formen' }
  },
  {
    id: 'kampfflaechen',
    icon: '🟦',
    title: 'Mattenflächen einrichten',
    description: 'Tatamis und Kampfflächen konfigurieren und Disziplinen zuweisen.',
    view: 'kampfflaechen',
    directAction: { label: 'Auto-generieren', icon: '⚡', type: 'auto_kampfflaechen' }
  },
  {
    id: 'zeitplan',
    icon: '⏰',
    title: 'Zeitplan erstellen',
    description: 'Ablaufplan erstellen und Tatamis mit Zeiten und Disziplinen belegen.',
    view: 'zeitplan',
    directAction: { label: 'Generieren', icon: '⚡', type: 'navigate_zeitplan' }
  },
  {
    id: 'kampfrichter',
    icon: '👨‍⚖️',
    title: 'Kampfrichter zuteilen',
    description: 'Kampfrichter anlegen und den Tatamis und Kategorien zuweisen.',
    view: 'staff-management'
  },
  {
    id: 'streaming',
    icon: '📡',
    title: 'Streaming vorbereiten',
    description: 'Live-Stream einrichten und Übertragungseinstellungen konfigurieren.',
    view: 'streaming'
  },
  {
    id: 'aufruf',
    icon: '📢',
    title: 'Aufruf-Display starten',
    description: 'Kampfaufruf-Display für Athleten und Zuschauer aktivieren.',
    view: null
  }
];

function WettkampftagChecklist({ isOpen, onClose, selectedTurnier, setActiveView }) {
  const turnierId = selectedTurnier?.turnier_id || selectedTurnier?.id || 'global';
  const storageKey = `wettkampftag_checklist_${turnierId}`;

  const [checked, setChecked] = useState({});
  const [actionLoading, setActionLoading] = useState({});
  const [actionMsg, setActionMsg] = useState({});

  useEffect(() => {
    if (isOpen) {
      try {
        const saved = JSON.parse(localStorage.getItem(storageKey) || '{}');
        setChecked(saved);
      } catch {
        setChecked({});
      }
      setActionMsg({});
    }
  }, [isOpen, storageKey]);

  const toggleItem = useCallback((id) => {
    setChecked(prev => {
      const next = { ...prev, [id]: !prev[id] };
      localStorage.setItem(storageKey, JSON.stringify(next));
      return next;
    });
  }, [storageKey]);

  const handleNavigate = useCallback((item) => {
    if (item.view) {
      setActiveView(item.view);
      onClose();
    } else if (item.id === 'aufruf' && selectedTurnier) {
      const tid = selectedTurnier.turnier_id || selectedTurnier.id;
      window.open(`/aufruf/${tid}`, '_blank');
    }
  }, [setActiveView, onClose, selectedTurnier]);

  const handleDirectAction = useCallback(async (item) => {
    if (!selectedTurnier) return;
    const tid = selectedTurnier.turnier_id || selectedTurnier.id;
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    setActionLoading(prev => ({ ...prev, [item.id]: true }));
    setActionMsg(prev => ({ ...prev, [item.id]: '' }));

    try {
      if (item.directAction.type === 'print_starterlisten') {
        const r = await fetch(`/api/druck/${tid}/startlisten`, { headers });
        const d = await r.json();
        if (d.success && d.divisionen?.length > 0) {
          printWindow(buildStartlistenHTML(d));
          setActionMsg(prev => ({ ...prev, [item.id]: `✓ ${d.divisionen.length} Divisionen` }));
        } else {
          setActionMsg(prev => ({ ...prev, [item.id]: 'Keine Daten vorhanden' }));
        }
      } else if (item.directAction.type === 'auto_brackets') {
        const r = await fetch(`/api/brackets/turnier/${tid}/auto-generate`, {
          method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }
        });
        const d = await r.json();
        setActionMsg(prev => ({ ...prev, [item.id]: d.success ? '✓ Brackets generiert' : (d.error || 'Fehler') }));
      } else if (item.directAction.type === 'auto_formen') {
        const r = await fetch(`/api/formen/auto-generate/${tid}`, {
          method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }
        });
        const d = await r.json();
        setActionMsg(prev => ({ ...prev, [item.id]: d.success ? '✓ Formen generiert' : (d.error || 'Fehler') }));
      } else if (item.directAction.type === 'auto_kampfflaechen') {
        const r = await fetch(`/api/kampfflaechen/${tid}/auto-generate`, {
          method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }
        });
        const d = await r.json();
        setActionMsg(prev => ({ ...prev, [item.id]: d.success ? '✓ Flächen generiert' : (d.error || 'Fehler') }));
      } else if (item.directAction.type === 'navigate_zeitplan') {
        setActiveView('zeitplan');
        onClose();
        return;
      }
    } catch (e) {
      setActionMsg(prev => ({ ...prev, [item.id]: 'Fehler aufgetreten' }));
    }
    setActionLoading(prev => ({ ...prev, [item.id]: false }));
  }, [selectedTurnier]);

  const resetAll = useCallback(() => {
    localStorage.removeItem(storageKey);
    setChecked({});
    setActionMsg({});
  }, [storageKey]);

  if (!isOpen) return null;

  const open = CHECKLIST_ITEMS.filter(i => !checked[i.id]);
  const done = CHECKLIST_ITEMS.filter(i => checked[i.id]);
  const sorted = [...open, ...done];
  const doneCount = done.length;
  const total = CHECKLIST_ITEMS.length;
  const progress = Math.round((doneCount / total) * 100);

  return ReactDOM.createPortal(
    <div className="wkt-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="wkt-modal">
        <div className="wkt-header">
          <div className="wkt-title-row">
            <div className="wkt-title-group">
              <span className="wkt-title-icon">🏅</span>
              <div>
                <h2>Wettkampftag-Vorbereitung</h2>
                {selectedTurnier && (
                  <p className="wkt-turnier-name">{selectedTurnier.name}</p>
                )}
              </div>
            </div>
            <button className="wkt-close-btn" onClick={onClose} aria-label="Schließen">✕</button>
          </div>

          <div className="wkt-progress-row">
            <div className="wkt-progress-bar">
              <div className="wkt-progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <span className="wkt-progress-label">{doneCount} / {total} erledigt</span>
          </div>
        </div>

        <div className="wkt-list">
          {sorted.map((item, idx) => {
            const isDone = !!checked[item.id];
            const isFirstDone = idx > 0 && !checked[sorted[idx - 1]?.id] && isDone;
            const loading = actionLoading[item.id];
            const msg = actionMsg[item.id];

            return (
              <React.Fragment key={item.id}>
                {isFirstDone && doneCount > 0 && open.length > 0 && (
                  <div className="wkt-divider"><span>Erledigt</span></div>
                )}
                <div className={`wkt-item ${isDone ? 'wkt-item--done' : ''}`}>
                  <button
                    className={`wkt-checkbox ${isDone ? 'wkt-checkbox--checked' : ''}`}
                    onClick={() => toggleItem(item.id)}
                    aria-label={isDone ? 'Als offen markieren' : 'Als erledigt markieren'}
                  >
                    {isDone ? '✓' : ''}
                  </button>

                  <div className="wkt-item-icon">{item.icon}</div>

                  <div className="wkt-item-content">
                    <span className="wkt-item-title">{item.title}</span>
                    <span className="wkt-item-desc">{item.description}</span>
                    {msg && (
                      <span className={`wkt-action-msg ${msg.startsWith('✓') ? 'wkt-action-msg--ok' : 'wkt-action-msg--err'}`}>
                        {msg}
                      </span>
                    )}
                  </div>

                  <div className="wkt-item-actions">
                    {item.directAction && (
                      <button
                        className="wkt-direct-btn"
                        onClick={() => handleDirectAction(item)}
                        disabled={!!loading}
                        title={item.directAction.label}
                      >
                        <span>{loading ? '⏳' : item.directAction.icon}</span>
                        <span>{item.directAction.label}</span>
                      </button>
                    )}
                    <button
                      className={`wkt-open-btn ${isDone ? 'wkt-open-btn--secondary' : ''}`}
                      onClick={() => handleNavigate(item)}
                      title="Öffnen"
                    >
                      →
                    </button>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {doneCount > 0 && (
          <div className="wkt-footer">
            <button className="wkt-reset-btn" onClick={resetAll}>Alle zurücksetzen</button>
            {doneCount === total && (
              <span className="wkt-all-done">🎉 Alles vorbereitet — viel Erfolg!</span>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

export default WettkampftagChecklist;
